import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Header from '../components/Header';
import LogService, { LOG_ACTIONS } from '../services/LogService';

const PAGE_SIZE = 30;

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function AdminFeedbacks() {
  const navigate = useNavigate();
  const logs = useMemo(() => LogService.getAll(), []);

  const [filters, setFilters] = useState({ startDate: '', endDate: '', type: '', userEmail: '' });
  const [page, setPage] = useState(0);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  // Feedback logs
  const allFeedbacks = useMemo(() => {
    return logs.filter((l) => l.action === LOG_ACTIONS.FEEDBACK_POSITIVE || l.action === LOG_ACTIONS.FEEDBACK_NEGATIVE);
  }, [logs]);

  const filteredFeedbacks = useMemo(() => {
    let result = [...allFeedbacks];
    if (filters.startDate) {
      const start = new Date(filters.startDate);
      result = result.filter((l) => new Date(l.timestamp) >= start);
    }
    if (filters.endDate) {
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((l) => new Date(l.timestamp) <= end);
    }
    if (filters.type === 'positive') {
      result = result.filter((l) => l.action === LOG_ACTIONS.FEEDBACK_POSITIVE);
    } else if (filters.type === 'negative') {
      result = result.filter((l) => l.action === LOG_ACTIONS.FEEDBACK_NEGATIVE);
    }
    if (filters.userEmail) {
      const search = filters.userEmail.toLowerCase();
      result = result.filter((l) => l.user_email.toLowerCase().includes(search));
    }
    return result;
  }, [allFeedbacks, filters]);

  const paginatedFeedbacks = filteredFeedbacks.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filteredFeedbacks.length / PAGE_SIZE);

  // Manual items
  const manualItemLogs = useMemo(() => {
    return logs.filter((l) => l.action === LOG_ACTIONS.MANUAL_ITEM_ADD);
  }, [logs]);

  // KPIs
  const totalFeedbacks = allFeedbacks.length;
  const positiveCount = allFeedbacks.filter((l) => l.action === LOG_ACTIONS.FEEDBACK_POSITIVE).length;
  const negativeCount = allFeedbacks.filter((l) => l.action === LOG_ACTIONS.FEEDBACK_NEGATIVE).length;
  const positivePct = totalFeedbacks > 0 ? Math.round((positiveCount / totalFeedbacks) * 100) : 0;
  const negativePct = totalFeedbacks > 0 ? Math.round((negativeCount / totalFeedbacks) * 100) : 0;
  const totalManualItems = manualItemLogs.length;

  // Ranking of most added manual items
  const manualRanking = useMemo(() => {
    const counts = {};
    manualItemLogs.forEach((l) => {
      const name = (l.details?.elemento || '').toLowerCase().trim();
      if (!name) return;
      if (!counts[name]) {
        counts[name] = { name: l.details.elemento, count: 0, categoria: l.details.categoria || '-' };
      }
      counts[name].count++;
    });
    return Object.values(counts).sort((a, b) => b.count - a.count).slice(0, 15);
  }, [manualItemLogs]);

  // Exports
  const handleExportFeedbacks = () => {
    const wsData = [
      ['Data/Hora', 'Email', 'Tipo', 'Comentário', 'Itens na Análise'],
      ...filteredFeedbacks.map((l) => [
        formatDate(l.timestamp),
        l.user_email,
        l.action === LOG_ACTIONS.FEEDBACK_POSITIVE ? 'Positivo' : 'Negativo',
        l.details?.feedback || '',
        l.details?.itemCount || '',
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 20 }, { wch: 25 }, { wch: 12 }, { wch: 50 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Feedbacks');
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `QuantoCustaObra_Feedbacks_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleExportManualItems = () => {
    const wsData = [
      ['Data/Hora', 'Email', 'Item', 'Categoria', 'Quantidade', 'Unidade', 'Observação'],
      ...manualItemLogs.map((l) => [
        formatDate(l.timestamp),
        l.user_email,
        l.details?.elemento || '',
        l.details?.categoria || '',
        l.details?.quantidade || '',
        l.details?.unidade || '',
        l.details?.observacao || '',
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 20 }, { wch: 25 }, { wch: 25 }, { wch: 18 }, { wch: 12 }, { wch: 10 }, { wch: 35 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Itens Manuais');
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `QuantoCustaObra_ItensManuais_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-6 md:py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Feedbacks</h1>
            <p className="text-gray-500">Avaliações dos usuários sobre os resultados</p>
          </div>
          <div className="flex gap-2 mt-3 sm:mt-0">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              Dashboard
            </button>
            <button
              onClick={() => navigate('/admin/logs')}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              Logs
            </button>
            <button
              onClick={() => navigate('/admin/usuarios')}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              Usuários
            </button>
            <button
              onClick={() => navigate('/admin/cub')}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              CUB
            </button>
            <button
              onClick={() => navigate('/admin/sinapi')}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              SINAPI
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalFeedbacks}</p>
            <p className="text-xs text-gray-400 mt-1">feedbacks recebidos</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-lg">👍</span>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Positivos</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{positiveCount} <span className="text-lg font-normal text-green-600">{positivePct}%</span></p>
            <p className="text-xs text-gray-400 mt-1">satisfeitos</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <span className="text-lg">👎</span>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Negativos</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{negativeCount} <span className="text-lg font-normal text-red-600">{negativePct}%</span></p>
            <p className="text-xs text-gray-400 mt-1">com ressalvas</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Manuais</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalManualItems}</p>
            <p className="text-xs text-gray-400 mt-1">itens adicionados</p>
          </div>
        </div>

        {/* Feedbacks Table */}
        <div className="bg-white rounded-xl border border-gray-200 mb-8">
          <div className="p-4 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Data início</label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => updateFilter('startDate', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Data fim</label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => updateFilter('endDate', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Tipo</label>
                  <select
                    value={filters.type}
                    onChange={(e) => updateFilter('type', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent"
                  >
                    <option value="">Todos</option>
                    <option value="positive">Positivo</option>
                    <option value="negative">Negativo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                  <input
                    type="text"
                    value={filters.userEmail}
                    onChange={(e) => updateFilter('userEmail', e.target.value)}
                    placeholder="Buscar..."
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent"
                  />
                </div>
              </div>
              <button
                onClick={handleExportFeedbacks}
                disabled={filteredFeedbacks.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Exportar Excel
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left">
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Data/Hora</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Usuário</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Tipo</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Comentário</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider text-right">Itens</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedFeedbacks.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-gray-400">Nenhum feedback encontrado</td>
                  </tr>
                ) : (
                  paginatedFeedbacks.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatDate(log.timestamp)}</td>
                      <td className="px-4 py-3 text-gray-700 max-w-[180px] truncate">{log.user_email}</td>
                      <td className="px-4 py-3">
                        {log.action === LOG_ACTIONS.FEEDBACK_POSITIVE ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            👍 Positivo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            👎 Negativo
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-[300px]">
                        {log.details?.feedback ? (
                          <span className="text-sm">{log.details.feedback}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-500">{log.details?.itemCount || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 p-4 border-t border-gray-100">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-500">{page + 1} de {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Próximo
              </button>
            </div>
          )}
        </div>

        {/* Manual Items Ranking */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Itens mais adicionados manualmente</h2>
              <p className="text-sm text-gray-500">Ranking dos itens que os usuários sentiram falta</p>
            </div>
            <button
              onClick={handleExportManualItems}
              disabled={manualItemLogs.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Exportar Excel
            </button>
          </div>

          {manualRanking.length === 0 ? (
            <div className="px-4 py-12 text-center text-gray-400">Nenhum item manual registrado</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left">
                    <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider w-16">#</th>
                    <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Item</th>
                    <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Categoria</th>
                    <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider text-right">Vezes adicionado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {manualRanking.map((item, i) => (
                    <tr key={item.name} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        {i < 3 ? (
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                            i === 0 ? 'bg-amber-100 text-amber-700' :
                            i === 1 ? 'bg-gray-200 text-gray-600' :
                            'bg-orange-100 text-orange-700'
                          }`}>
                            {i + 1}
                          </span>
                        ) : (
                          <span className="text-gray-400 font-medium pl-2">{i + 1}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{item.name}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-medium text-orca-700 bg-orca-50 px-2 py-1 rounded-md">{item.categoria}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-lg font-bold text-gray-900">{item.count}</span>
                        <span className="text-gray-400 text-xs ml-1">{item.count === 1 ? 'vez' : 'vezes'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminFeedbacks;
